import { useState } from "react";
import Icon from "./Icon";

const ENGINE_LABELS = ["EF7P", "EF7PTC", "EF7TC", "TU3", "TU5P", "UNKNOWN"];

function PredictionFeedback({ predictedLabel, disabled, saving, onSave }) {
  const [label, setLabel] = useState("");
  const [savedLabel, setSavedLabel] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (!label || disabled || submitting || label === savedLabel) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await onSave(label);
      if (result) setSavedLabel(result.corrected_label);
    } catch (failure) {
      setError(failure.response?.data?.error || "The correction could not be saved. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return <form className="voice-feedback" onSubmit={submit} aria-labelledby="feedback-heading">
    <h3 id="feedback-heading">Was this prediction wrong?</h3>
    <p>Your correction will be saved with this recording.</p>
    <div className="voice-field"><label htmlFor="correct-engine-label">Correct engine label</label>
      <select id="correct-engine-label" value={label} onChange={(event) => { setLabel(event.target.value); setError(""); }} disabled={disabled || submitting}>
        <option value="">Select the correct engine</option>
        {ENGINE_LABELS.filter((engine) => engine !== predictedLabel).map((engine) => <option key={engine} value={engine}>{engine}</option>)}
      </select>
    </div>
    <button type="submit" className="voice-button secondary" disabled={!label || disabled || submitting || label === savedLabel}>{saving || submitting ? "Saving correction…" : "Save correction"}</button>
    {error && <p className="voice-inline-error" role="alert">{error}</p>}
    {savedLabel && <p className="voice-form-status" role="status">Correction saved: {savedLabel}.</p>}
  </form>;
}

function ModelResult({ result, heading }) {
  if (result.error) return <section className="voice-model-result"><h3>{heading}</h3><p className="voice-inline-error" role="alert">{result.error}</p></section>;
  return <section className="voice-model-result" aria-label={heading || "Engine prediction"}>
    {heading && <h3>{heading}</h3>}
    <div className="voice-result-core"><div><p className="voice-result-label">Predicted engine</p><strong className="voice-engine">{result.engine_type}</strong></div><div className="voice-confidence-area"><div className="voice-confidence">{(result.confidence * 100).toFixed(1)}%</div><p className="voice-result-label">Model confidence</p></div></div>
    <details className="voice-details"><summary>Prediction details<Icon name="chevron" /></summary><div className="voice-probabilities">{Object.entries(result.probabilities || {}).sort((a, b) => Number(b[1]) - Number(a[1])).map(([engine, probability]) => {
      const percentage = Math.max(0, Math.min(100, Number(probability) * 100));
      return <div className="voice-probability" key={engine}><span>{engine}</span><div className="voice-probability-track"><span style={{ width: `${percentage}%` }} /></div><span>{percentage.toFixed(1)}%</span></div>;
    })}</div><p className="voice-segments">{result.segments} audio {result.segments === 1 ? "segment" : "segments"} analysed</p></details>
  </section>;
}

export default function PredictionPanel({ inferenceResult, experimentalResult, modelLabels, comparisonMode, workshopMode, predictionAudio, audioUrl, busy, predictionId, disabled, feedbackSaving, onSaveCorrection }) {
  const extension = { "audio/mp4": "m4a", "audio/aac": "aac", "audio/ogg": "ogg", "audio/wav": "wav", "audio/x-wav": "wav", "audio/mpeg": "mp3" }[predictionAudio?.type.split(";")[0]] || "webm";
  const filename = predictionAudio?.name || `prediction-audio.${extension}`;
  return <section className="voice-panel voice-result" aria-labelledby="result-heading" aria-busy={busy}>
    <div className="voice-panel-head"><h2 id="result-heading">Prediction result</h2>{inferenceResult && <span className="voice-state"><span className="voice-state-dot" />Ready</span>}</div>
    {inferenceResult ? <>
      <div aria-live="polite"><ModelResult result={inferenceResult} heading={comparisonMode || workshopMode ? modelLabels?.primary || "Current model" : null} />{comparisonMode && experimentalResult && <ModelResult result={experimentalResult} heading={modelLabels?.comparison || "Experimental model"} />}</div>
      {predictionId && <p className="voice-saved-note" role="status">Recording and prediction saved on the server.</p>}
      {predictionId && !inferenceResult.error && <PredictionFeedback key={predictionId} predictedLabel={inferenceResult.engine_type} disabled={disabled} saving={feedbackSaving} onSave={onSaveCorrection} />}
      {audioUrl && predictionAudio && <><a href={audioUrl} download={filename} className="voice-button secondary"><Icon name="download" />Download recording</a><p className="voice-download-note">The audio used for this prediction.</p></>}
    </> : <div className="voice-empty-result" role="status"><Icon name="sound" /><h3>{busy ? "Analysing engine sound" : "Your result will appear here"}</h3><p>{busy ? "Processing the captured audio…" : "Capture audio, then predict the engine type."}</p></div>}
  </section>;
}
